import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, Injector, input, output, signal } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NeoFormFieldModule } from '@neogov/pattern-library/neo-form-field';
import { NeoSelectModule } from '@neogov/pattern-library/neo-select';
import {
  FieldErrorsComponent,
  OuterControlErrorStateMatcher,
  shouldSurfaceFieldError,
} from '../field-errors';
import { SpinnerComponent } from '../spinner';

export interface SelectOption {
  value: string;
  label: string;
  subtext?: string;
  disabled?: boolean;
  color?: string;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

@Component({
  selector: 'cm-select-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NeoFormFieldModule,
    NeoSelectModule,
    FieldErrorsComponent,
    SpinnerComponent,
  ],
  templateUrl: './select-field.component.html',
  styleUrl: './select-field.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectFieldComponent,
      multi: true,
    },
  ],
})
export class SelectFieldComponent implements ControlValueAccessor {
  private readonly _cdr = inject(ChangeDetectorRef);

  public readonly label = input<string>('');
  public readonly hideLabel = input<boolean>(false);
  public readonly ariaLabel = input<string>('');
  public readonly placeholder = input<string>('');
  public readonly autId = input.required<string>();
  public readonly multiple = input<boolean>(false);
  public readonly options = input<SelectOption[]>([]);
  public readonly optionGroups = input<SelectOptionGroup[]>([]);
  public readonly hint = input<string>('');
  public readonly required = input<boolean>(false);

  // Error handling inputs
  public readonly errorMessages = input<Partial<Record<string, string>>>({});
  public readonly showErrorsWhen = input<'touched' | 'dirty' | 'always'>('touched');
  public readonly hideErrors = input<boolean>(false);

  /** Whether the field is disabled via direct input binding. */
  public readonly disabled = input<boolean>(false);

  /** Shows a spinner inside the open panel while options are being fetched. */
  public readonly loading = input<boolean>(false);

  /** How space is reserved for hint/error text. 'fixed' reserves one line; 'dynamic' grows as needed. */
  public readonly subscriptSizing = input<'fixed' | 'dynamic'>('fixed');

  /** Emits the panel open/close state from the underlying neo-select. */
  public readonly openedChange = output<boolean>();

  public readonly effectiveAriaLabel = computed(
    () => this.ariaLabel() || (this.hideLabel() ? this.label() || this.placeholder() : ''),
  );

  private readonly _value = signal<string | string[] | null>(null);

  public get value(): string | string[] | null {
    return this._value();
  }

  public set value(v: string | string[] | null) {
    this._value.set(v);
  }

  protected readonly selectedOption = computed(() => this.getSelectedOption());
  protected readonly multiSelectDisplay = computed(() => this.getMultiSelectDisplay());

  private _cvaDisabled = false;

  /** Effective disabled state — true if disabled by input or by CVA (reactive form control). */
  public get isDisabled(): boolean {
    return this.disabled() || this._cvaDisabled;
  }

  private readonly _injector = inject(Injector);
  public readonly ngControl = signal<NgControl | null>(null);

  /**
   * Feeds the OUTER control's validity into neo-select so it exposes
   * aria-invalid and links the error via aria-describedby
   * (WCAG 3.3.1 / 4.1.2). See {@link OuterControlErrorStateMatcher}.
   */
  protected readonly errorStateMatcher = new OuterControlErrorStateMatcher(() =>
    shouldSurfaceFieldError(this.ngControl()?.control, this.showErrorsWhen(), this.hideErrors())
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (value: string | string[] | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  constructor() {
    // Defer NgControl lookup to avoid circular dependency
    // The NgControl directive will be available after the component is constructed
    queueMicrotask(() => {
      try {
        const ngControl =
          this._injector.get(NgControl, null, { optional: true, self: true }) || null;
        this.ngControl.set(ngControl);
      } catch {
        this.ngControl.set(null);
      }
    });
  }

  public writeValue(value: string | string[] | null): void {
    this.value = value;
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._cvaDisabled = isDisabled;
    this._cdr.markForCheck();
  }

  public onSelectChange(value: string | string[]): void {
    this.value = value;
    this.onChange(this.value);
  }

  public onBlur(): void {
    // Simply call onTouched - no setTimeout needed!
    // The cm-field-errors component is subscribed to control.events
    // which emits TouchedChangeEvent synchronously when markAsTouched() is called
    this.onTouched();
  }

  /**
   * Compare function for neo-select to properly match option values
   * Required to prevent neo-select from emitting internal UUIDs instead of actual values
   * Uses non-mutating sort to avoid side effects on bound arrays
   */
  public compareValues = (v1: string | string[] | null, v2: string | string[] | null): boolean => {
    if (Array.isArray(v1) && Array.isArray(v2)) {
      return JSON.stringify([...v1].sort()) === JSON.stringify([...v2].sort());
    }
    return v1 === v2;
  };

  /**
   * Gets the selected option for single-select display
   */
  public getSelectedOption(): SelectOption | null {
    if (this.value === null || this.value === undefined || Array.isArray(this.value)) {
      return null;
    }

    // Check flat options first
    const flatOption = this.options().find(opt => opt.value === this.value);
    if (flatOption) return flatOption;

    // Check grouped options
    for (const group of this.optionGroups()) {
      const option = group.options.find(opt => opt.value === this.value);
      if (option) return option;
    }

    return null;
  }

  /**
   * Gets the display text for multi-select
   * Returns comma-separated labels or count if too many
   */
  public getMultiSelectDisplay(): string {
    if (!Array.isArray(this.value) || this.value.length === 0) {
      return '';
    }

    // Collect all options from flat and grouped
    const allOptions: SelectOption[] = [
      ...this.options(),
      ...this.optionGroups().flatMap(group => group.options)
    ];

    const selectedOptions = allOptions.filter(opt =>
      this.value && Array.isArray(this.value) && this.value.includes(opt.value)
    );

    if (selectedOptions.length === 0) {
      return '';
    }

    // If 1-2 items, show labels
    if (selectedOptions.length <= 2) {
      return selectedOptions.map(opt => opt.label).join(', ');
    }

    // If 3+ items, show count
    return `${selectedOptions.length} selected`;
  }
}
